import React, { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Users } from "lucide-react";

export default function AdminChats() {
  const [rooms, setRooms] = useState([]);
  const [members, setMembers] = useState([]);
  const [active, setActive] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", member_ids: [] });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const confirmRoom = rooms.find((r) => r.id === confirmDeleteId);

  const load = async () => {
    const [r, m] = await Promise.all([api.get("/chat/rooms/admin/all"), api.get("/members/all")]);
    setRooms(r.data || []); setMembers(m.data || []);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try { await api.post("/chat/rooms", form); toast.success("Room created"); setNewOpen(false); setForm({ name: "", description: "", member_ids: [] }); load(); }
    catch { toast.error("Create failed"); }
  };

  const saveActive = async () => {
    if (!active) return;
    try { await api.put(`/chat/rooms/${active.id}`, { name: active.name, description: active.description || "", member_ids: active.member_ids || [] }); toast.success("Saved"); load(); }
    catch { toast.error("Save failed"); }
  };
  const remove = () => { if (active) setConfirmDeleteId(active.id); };
  const removeFromList = (id) => setConfirmDeleteId(id);

  const performDelete = async () => {
    const id = confirmDeleteId;
    if (!id) return;
    setConfirmDeleteId(null);
    try {
      await api.delete(`/chat/rooms/${id}`);
      toast.success("Room deleted");
      if (active?.id === id) setActive(null);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Delete failed";
      toast.error(msg);
      console.error("delete room failed", err);
    }
  };

  const toggleMember = (uid) => setActive((r) => {
    const ids = r.member_ids || [];
    return { ...r, member_ids: ids.includes(uid) ? ids.filter((x) => x !== uid) : [...ids, uid] };
  });

  return (
    <AppShell variant="admin" title="Group chats" actions={
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogTrigger asChild><Button className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-fg))]" data-testid="chats-new"><Plus className="h-4 w-4 mr-2" /> New room</Button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>Create chat room</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} data-testid="new-chat-name" /></div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))} /></div>
            <div>
              <Label>Members</Label>
              <div className="max-h-44 overflow-y-auto border border-border rounded-md p-2 mt-1">
                {members.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 py-1 text-sm">
                    <Checkbox checked={form.member_ids.includes(m.id)} onCheckedChange={(v) => setForm((s) => ({ ...s, member_ids: v ? [...s.member_ids, m.id] : s.member_ids.filter((x) => x !== m.id) }))} />
                    <span>{m.first_name} {m.last_name} <span className="text-xs text-muted-foreground">({m.member_id || m.role})</span></span>
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={create} className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))]" data-testid="new-chat-create">Create</Button>
          </div>
        </DialogContent>
      </Dialog>
    }>
      <div className="grid lg:grid-cols-[260px_1fr] gap-5">
        <Card className="p-3 h-fit">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Rooms</div>
          {rooms.length === 0 && <div className="text-sm text-muted-foreground p-2">No rooms yet.</div>}
          {rooms.map((r) => (
            <div key={r.id} className={`group flex items-center gap-1 rounded-lg mb-1 ${active?.id === r.id ? "bg-[hsl(var(--secondary))]" : ""}`}>
              <button onClick={() => setActive(r)} className={`flex-1 text-left px-3 py-2 rounded-lg text-sm transition-colors ${active?.id === r.id ? "text-[hsl(var(--primary))] font-medium" : "hover:bg-[hsl(var(--secondary))]/60"}`} data-testid={`chats-list-${r.id}`}>
                <div className="truncate">{r.name}</div>
                <div className="text-[11px] text-muted-foreground"><Users className="h-3 w-3 inline" /> {r.member_ids?.length || 0} members</div>
              </button>
              <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); removeFromList(r.id); }} className="h-8 w-8 mr-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10" title="Delete room" data-testid={`chats-list-delete-${r.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </Card>
        <div>
          {!active ? <Card className="p-10 text-center text-muted-foreground">Pick a room or create a new one.</Card> : (
            <Card className="p-4 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={active.name} onChange={(e) => setActive((r) => ({ ...r, name: e.target.value }))} /></div>
                <div className="sm:col-span-2"><Label>Description</Label><Textarea rows={2} value={active.description || ""} onChange={(e) => setActive((r) => ({ ...r, description: e.target.value }))} /></div>
              </div>
              <div>
                <Label>Members ({(active.member_ids || []).length})</Label>
                <div className="mt-2 max-h-72 overflow-y-auto border border-border rounded-md p-2 grid sm:grid-cols-2 gap-1">
                  {members.map((m) => (
                    <label key={m.id} className="flex items-center gap-2 py-1 px-2 text-sm hover:bg-[hsl(var(--secondary))]/60 rounded">
                      <Checkbox checked={(active.member_ids || []).includes(m.id)} onCheckedChange={() => toggleMember(m.id)} data-testid={`chat-member-toggle-${m.id}`} />
                      <span>{m.first_name} {m.last_name} <span className="text-xs text-muted-foreground">({m.member_id || m.role})</span></span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex justify-between">
                <Button variant="outline" onClick={remove} data-testid="chats-delete" className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-4 w-4 mr-1" /> Delete room</Button>
                <Button onClick={saveActive} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))]" data-testid="chats-save">Save changes</Button>
              </div>
            </Card>
          )}
        </div>
      </div>

      <AlertDialog open={!!confirmDeleteId} onOpenChange={(o) => !o && setConfirmDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete chat room?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <span className="font-semibold text-foreground">{confirmRoom?.name || "this room"}</span> and all of its messages. Members will no longer have access to it. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={performDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="chats-delete-confirm">Delete room</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
